import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import { Response } from "../interfaces";
import { Campaign } from "../campaign/campaignTypes";

export type AutomatedGiftTab = "birthday" | "home-anniversary";

export interface AutomatedGiftsPayload {
  birthday: Campaign;
  home_anniversary: Campaign;
}

export interface AddAutomatedRecipientBody {
  name: string;
  email: string;
  mobile?: string;
  event_date: string;
  gift_type?: string;
  price_id?: string;
}

export interface UpdateAutomatedCampaignBody {
  gift_type?: string;
  price_id?: string | null;
  handwritten_note?: string | null;
  apply_to_all_recipients?: boolean;
  apply_scope?: "gift_type" | "price" | "both";
}

export interface UpdateAutomatedRecipientBody {
  gift_type?: string;
  price_id?: string | null;
  name?: string;
  mobile?: string;
  event_date?: string;
  is_active?: boolean;
}

export const automatedGiftsApi = createApi({
  reducerPath: "automatedGiftsApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["automatedGifts"],
  endpoints: (builder) => ({
    getAutomatedGifts: builder.query<Response<AutomatedGiftsPayload>, void>({
      query: () => ({
        url: "automated-gifts",
        method: "GET",
      }),
      providesTags: ["automatedGifts"],
    }),
    setAutomatedGiftStatus: builder.mutation<
      Response<Campaign>,
      { type: AutomatedGiftTab; is_active: boolean }
    >({
      query: ({ type, is_active }) => ({
        url: `automated-gifts/${type}/status`,
        method: "PATCH",
        data: { is_active },
      }),
      invalidatesTags: ["automatedGifts"],
    }),
    updateAutomatedCampaignSettings: builder.mutation<
      Response<Campaign>,
      { type: AutomatedGiftTab; data: UpdateAutomatedCampaignBody }
    >({
      query: ({ type, data }) => ({
        url: `automated-gifts/${type}/settings`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["automatedGifts"],
    }),
    addAutomatedRecipient: builder.mutation<
      Response<Campaign>,
      { type: AutomatedGiftTab; data: AddAutomatedRecipientBody }
    >({
      query: ({ type, data }) => ({
        url: `automated-gifts/${type}/recipients`,
        method: "POST",
        data,
      }),
      invalidatesTags: ["automatedGifts"],
    }),
    updateAutomatedRecipient: builder.mutation<
      Response<Campaign>,
      {
        type: AutomatedGiftTab;
        recipientId: string;
        data: UpdateAutomatedRecipientBody;
      }
    >({
      query: ({ type, recipientId, data }) => ({
        url: `automated-gifts/${type}/recipients/${recipientId}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["automatedGifts"],
    }),
    removeAutomatedRecipient: builder.mutation<
      Response<{ ok: boolean }>,
      { type: AutomatedGiftTab; recipientId: string }
    >({
      query: ({ type, recipientId }) => ({
        url: `automated-gifts/${type}/recipients/${recipientId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["automatedGifts"],
    }),
  }),
});

export const {
  useGetAutomatedGiftsQuery,
  useSetAutomatedGiftStatusMutation,
  useUpdateAutomatedCampaignSettingsMutation,
  useAddAutomatedRecipientMutation,
  useUpdateAutomatedRecipientMutation,
  useRemoveAutomatedRecipientMutation,
} = automatedGiftsApi;
